<template>
  <USelect
    v-model="selectedLocale"
    :items="localeOptions"
    value-key="value"
    label-key="label"
    :ui="{
      base: `bg-transparent border-0 shadow-none ring-0 focus:ring-0 focus:border-0 hover:bg-(--ui-bg-elevated)/40 rounded-md transition-all cursor-pointer outline-none ${props.collapsed ? '!ps-2.5 !pe-2.5' : ''}`,
      value: props.collapsed ? 'hidden' : 'text-default',
      trailingIcon: props.collapsed ? 'hidden' : '',
      leading: props.collapsed ? 'relative inset-auto flex items-center !ps-0' : '',
      content: 'min-w-[160px]',
      itemTrailingIcon: 'hidden',
      item: 'cursor-pointer items-center'
    }"
    class="language-switcher"
  >
    <template #leading>
      <span v-if="selectedItem?.flag" class="language-flag-wrapper">
        <UIcon :name="selectedItem.flag" mode="svg" class="language-flag" />
      </span>
    </template>
    
    <template #item-leading="{ item }">
      <span v-if="item.flag" class="language-flag-wrapper">
        <UIcon :name="item.flag" mode="svg" class="language-flag" />
      </span>
    </template>

    <template #item-trailing="{ item }">
      <UIcon 
        v-if="item.value === selectedLocale"
        name="i-lucide-check"
        class="language-check"
      />
    </template>
  </USelect>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useLocale } from '@/store';
import { useI18n } from 'vue-i18n'

const props = withDefaults(defineProps<{
  collapsed?: boolean
}>(), {
  collapsed: false
})

const localeStore = useLocale()
const { t, locale } = useI18n({ useScope: 'global' })

const localeConfig: Record<string, { full: string; flag: string }> = {
  en: { full: 'English', flag: 'i-flag-gb-4x3' },
  cs: { full: 'Čeština', flag: 'i-flag-cz-4x3' },
  fi: { full: 'Suomi', flag: 'i-flag-fi-4x3' }
}

const localeOptions = computed(() =>
  localeStore.availableLocales.map((code) => ({
    value: code,
    label: localeConfig[code]?.full || code.toUpperCase(),
    flag: localeConfig[code]?.flag || ''
  }))
)

const selectedLocale = computed({
  get: () => localeStore.currentLocale,
  set: (value: string) => {
    if (!value) return
    localeStore.setLocale(value)
    locale.value = value
  }
})

const selectedItem = computed(() => 
    localeOptions.value.find((item) => item.value === selectedLocale.value)
)
</script>

<style scoped>
.language-switcher {
  display: inline-flex;
}

.language-flag-wrapper {
  display: inline-flex;
  border-radius: 2px;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
  line-height: 0;
}

:root.dark .language-flag-wrapper {
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.2);
}

.language-flag {
  width: 20px;
  height: 15px;
}

.language-check {
  width: 16px;
  height: 16px;
  color: var(--ui-text-dimmed);
}
</style>